import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AdjusterSelect({ value, onChange, label = 'Adjuster Name', disabled, companyOriginalId }) {
  const [query, setQuery] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAdjuster, setNewAdjuster] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const token = localStorage.getItem('token');

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync value prop
  useEffect(() => { setQuery(value || ''); }, [value]);

  const search = async (q) => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/adjusters`;
      const params = [];
      if (q) params.push(`q=${encodeURIComponent(q)}`);
      if (companyOriginalId) params.push(`company_id=${encodeURIComponent(companyOriginalId)}`);
      if (params.length) url += '?' + params.join('&');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setOptions(Array.isArray(data) ? data.slice(0, 8) : []);
    } catch {
      setOptions([]);
    }
    setLoading(false);
  };

  // Re-search when companyOriginalId changes
  useEffect(() => {
    if (showDropdown) search(query || '');
  }, [companyOriginalId]);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setShowDropdown(true);
    search(v);
    onChange(null, v, '', ''); // free text
  };

  const select = (opt) => {
    setQuery(opt.name);
    setShowDropdown(false);
    onChange(opt.id, opt.name, opt.email || '', opt.phone || '');
  };

  const addNew = async () => {
    if (!newAdjuster.name.trim()) return;
    const res = await fetch(`${API_URL}/api/adjusters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(newAdjuster)
    });
    const created = await res.json();
    select(created);
    setShowAddForm(false);
    setNewAdjuster({ name: '', email: '', phone: '' });
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => { setShowDropdown(true); search(query || ""); }}
          disabled={disabled}
          placeholder="Type to search or select..."
          className={`w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${disabled ? 'bg-gray-50' : ''}`}
        />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); setShowDropdown(v => !v); if (!showDropdown) search(query || ""); }}
          disabled={disabled}
          className="absolute right-1 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {showDropdown && (
        <div className="absolute z-50 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 max-h-48 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading...</div>}
          {!loading && options.map(opt => (
            <div key={opt.id} onMouseDown={() => select(opt)}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm">
              {opt.name}
              {opt.email && <span className="text-xs text-gray-400 ml-2">- {opt.email}</span>}
            </div>
          ))}
          {!loading && options.length === 0 && query.length >= 1 && (
            <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
          )}
          <div className="border-t px-3 py-2">
            {!showAddForm ? (
              <button onMouseDown={() => setShowAddForm(true)} className="text-xs text-blue-600 hover:underline">+ Add New Adjuster</button>
            ) : (
              <div className="space-y-1">
                <input
                  value={newAdjuster.name}
                  onChange={e => setNewAdjuster(p => ({ ...p, name: e.target.value }))}
                  placeholder="Name *"
                  className="w-full px-2 py-1 text-xs border rounded"
                />
                <input
                  value={newAdjuster.email}
                  onChange={e => setNewAdjuster(p => ({ ...p, email: e.target.value }))}
                  placeholder="Email"
                  className="w-full px-2 py-1 text-xs border rounded"
                />
                <input
                  value={newAdjuster.phone}
                  onChange={e => setNewAdjuster(p => ({ ...p, phone: e.target.value }))}
                  placeholder="Phone"
                  className="w-full px-2 py-1 text-xs border rounded"
                />
                <div className="flex gap-1">
                  <button onMouseDown={addNew} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">Add</button>
                  <button onMouseDown={() => setShowAddForm(false)} className="px-2 py-1 text-xs bg-gray-200 rounded">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
